import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { AssetCategory, CreateAssetCategoryDTO, UpdateAssetCategoryDTO } from '@yawi/types';
import { DEFAULT_ASSET_CATEGORIES } from '../../constants/categories';

// Flag to enable/disable API-based categories
// Set to true when the backend endpoint is ready
const USE_API_CATEGORIES = false;

export function useAssetCategories() {
  const { inventoryRepo } = useServices();
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (USE_API_CATEGORIES) {
        // When API is ready, use this:
        const result = await inventoryRepo.getCategories();
        setCategories(result);
      } else {
        // Use static categories for now
        setCategories(DEFAULT_ASSET_CATEGORIES);
      }
    } catch (err) {
      // Fallback to static categories on error
      console.warn('Failed to fetch categories from API, using defaults:', err);
      setCategories(DEFAULT_ASSET_CATEGORIES);
    } finally {
      setLoading(false);
    }
  }, [inventoryRepo]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = useCallback(async (data: CreateAssetCategoryDTO) => {
    if (!USE_API_CATEGORIES) {
      // For static mode, just add to local state
      const newCategory: AssetCategory = {
        id: `cat-${Date.now()}`,
        name: data.name,
        description: data.description || null,
        parentCategoryId: data.parentCategoryId || null,
        assetCount: 0,
        createdAt: new Date().toISOString(),
      };
      setCategories(prev => [...prev, newCategory]);
      return newCategory;
    }
    const newCategory = await inventoryRepo.createCategory(data);
    fetchCategories();
    return newCategory;
  }, [inventoryRepo, fetchCategories]);

  const updateCategory = useCallback(async (id: string, data: UpdateAssetCategoryDTO) => {
    if (!USE_API_CATEGORIES) {
      // For static mode, update local state
      setCategories(prev => prev.map(cat =>
        cat.id === id ? { ...cat, ...data } : cat
      ));
      return { id, ...data } as AssetCategory;
    }
    const updated = await inventoryRepo.updateCategory(id, data);
    fetchCategories();
    return updated;
  }, [inventoryRepo, fetchCategories]);

  const deleteCategory = useCallback(async (id: string) => {
    if (!USE_API_CATEGORIES) {
      // For static mode, remove from local state
      setCategories(prev => prev.filter(cat => cat.id !== id));
      return;
    }
    await inventoryRepo.deleteCategory(id);
    fetchCategories();
  }, [inventoryRepo, fetchCategories]);

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    refresh: fetchCategories,
  };
}
